import type { AssistantPlanStep } from '../client/api';
import { assistantRoleCan, type AssistantCapabilityPermission } from '@cornerstone/shared';

// =============================================================================
// Page awareness for the app-wide assistant: which screen the operator is on
// (sent to the planner as a hint + drives example chips) and which store
// loaders to refresh after confirmed steps ran.
// =============================================================================

export type AssistantPage = 'schedule' | 'media' | 'approval' | 'org' | 'devices' | 'history' | 'other';

/** `search` is the router query (e.g. "?section=review" opens 승인 관리 over the schedule). */
export function pageHint(pathname: string, search = ''): AssistantPage {
  const p = pathname.replace(/^#/, '');
  if (p.startsWith('/schedule')) return new URLSearchParams(search).has('section') ? 'approval' : 'schedule';
  if (p.startsWith('/media') || p.startsWith('/playlists')) return 'media';
  if (p.startsWith('/approval')) return 'approval';
  if (p === '/org' || p.startsWith('/org/')) return 'org';
  if (p.startsWith('/devices')) return 'devices';
  if (p.startsWith('/history')) return 'history';
  return 'other';
}

/** A starter chip. Plain text is a question anyone may ask; `needs` hides it from roles that could not run it. */
export type AssistantExample = string | { text: string; needs: AssistantCapabilityPermission };
const write = (text: string): AssistantExample => ({ text, needs: 'content:write' });
const approve = (text: string): AssistantExample => ({ text, needs: 'content:approve' });

const COMMON = ['지금 스케줄 확인해줘'];
const DEVICES = '기기 상태 확인해줘';
const EXAMPLES: Record<AssistantPage, AssistantExample[]> = {
  schedule: [...COMMON, write('평일 12시부터 1시까지 로비에 점심 플레이리스트 틀어줘'), write('점심 편성을 1시부터 2시로 바꿔줘')],
  // 이름 변경은 그룹 권한 모드에 없어서, 두 모드에서 다 되는 조회로 둔다.
  media: ['플레이리스트는 어떻게 만들어?', write('점심 플레이리스트 평일 12시부터 1시까지 틀어줘'), '점심 플레이리스트는 지금 어디에 편성돼 있어?'],
  approval: [...COMMON, approve('승인 대기 중인 스케줄 모두 승인해줘'), approve('"승인 대기 안내" 스케줄 반려해줘, 사유는 시간 재확인')],
  org: [DEVICES, '꺼져 있거나 장애 난 기기 있어?', '디바이스는 어떻게 추가해?'],
  devices: [DEVICES, write('다음주 금요일 3시부터 4시까지 이 그룹에 신제품 영상 틀어줘'), '디바이스 상세 정보는 어디서 봐?'],
  history: [...COMMON, '최근에 초안으로 저장된 스케줄 있어?'],
  other: [...COMMON, DEVICES, write('평일 12시부터 1시까지 로비에 점심 플레이리스트 틀어줘'), '할 수 있는 일 알려줘'],
};
export function examplesFor(page: AssistantPage): AssistantExample[] {
  return EXAMPLES[page];
}

/** Chip texts this role may run. Unknown role → questions only (display only — routes enforce). */
export function allowedExamples(examples: readonly AssistantExample[], role: string | null | undefined): string[] {
  return examples.flatMap((e) => (typeof e === 'string' ? [e] : assistantRoleCan(role, e.needs) ? [e.text] : []));
}

export type Loaders = Partial<Record<'loadSchedules' | 'loadTimelineBlocks' | 'loadScheduleTypes' | 'loadPlaylists' | 'loadNotices', () => Promise<void>>>;

/** Which loaders a set of executed steps invalidates. Returns loader names (deterministic order). */
export function loadersFor(steps: Array<Pick<AssistantPlanStep, 'path'>>): Array<keyof Loaders> {
  const out = new Set<keyof Loaders>();
  for (const s of steps) {
    if (s.path.startsWith('/api/schedules')) out.add('loadSchedules');
    else if (s.path.startsWith('/api/timeline-blocks')) out.add('loadTimelineBlocks');
    else if (s.path.startsWith('/api/schedule-types')) { out.add('loadScheduleTypes'); out.add('loadTimelineBlocks'); }
    else if (s.path.startsWith('/api/playlists')) out.add('loadPlaylists');
    else if (s.path.startsWith('/api/notices')) out.add('loadNotices');
  }
  return [...out];
}

/** Broadcast after assistant steps touched assignments — the 스케줄 calendar reloads on it. */
export const ASSIGNMENTS_CHANGED_EVENT = 'cst:schedule-assignments-changed';

export function refreshAfter(steps: Array<Pick<AssistantPlanStep, 'path'>>, loaders: Loaders): void {
  for (const name of loadersFor(steps)) void loaders[name]?.();
  if (steps.some((s) => s.path.startsWith('/api/schedule-assignments')) && typeof window !== 'undefined') {
    window.dispatchEvent(new Event(ASSIGNMENTS_CHANGED_EVENT));
  }
}
